<template>
    <v-container>
        <v-container grid-list-md fluid class="pt-0 pb-0 pl-0 pr-0" text-xs-right>
            <v-layout row wrap>
                <v-flex xs12 xl12>
                    <p class="text-xs-left display-1 mb-4 pt-4">{{$t('paymentHistory') | capitalize}}</p>
                </v-flex>
            </v-layout>
        </v-container>
        <v-container grid-list-md fluid class="pl-0 pr-0" text-xs-left>
            <v-layout row wrap>
                <v-flex xl4 lg4 md6 xs12>
                    <v-menu
                        v-model="displayFromDatePicker"
                        :close-on-content-click="false"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        max-width="290px"
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="computedFromDateFormatted"
                                :label="$filters.capitalize($t('from'))"
                                persistent-hint
                                readonly
                                v-on="on"
                                :append-icon="fromDate ? 'close' : ''"
                                @click:append="clearFromDate"
                            ></v-text-field>
                        </template>
                        <v-date-picker v-model="fromDate" no-title @input="displayFromDatePicker = false" :max="toDate"
                                       @change="getPaymentHistory"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-flex xl4 lg4 md6 xs12>
                    <v-menu
                        v-model="displayToDatePicker"
                        :close-on-content-click="false"
                        lazy
                        transition="scale-transition"
                        offset-y
                        full-width
                        max-width="290px"
                        min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="computedToDateFormatted"
                                :label="$filters.capitalize($t('to'))"
                                persistent-hint
                                readonly
                                v-on="on"
                                :append-icon="toDate ? 'close' : ''"
                                @click:append="clearToDate"
                            ></v-text-field>
                        </template>
                        <v-date-picker v-model="toDate" no-title @input="displayToDatePicker = false" :min="fromDate"
                                       :max="today" @change="getPaymentHistory"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-flex xl4 lg4 md6 xs12>
                    <v-select
                        :items="projects"
                        v-model="projectId"
                        :label="$filters.capitalize($t('project'))"
                        item-text="name"
                        item-value="id"
                        clearable
                        @change="getPaymentHistory"
                    ></v-select>
                </v-flex>
                <v-flex xl12 xs12>
                    <v-data-table
                        :headers="headers"
                        :items="dataTable"
                        :class="{'data-table-mobile': $utils.isMobile(),'elevation-1': $utils.isNotMobile()}"
                        :pagination.sync="pagination"
                        :total-items="pagination.totalItems"
                        :rows-per-page-items="pagination.rowsPerPageItems"
                        :rows-per-page-text="$filters.capitalize($t('rowsPerPage'))"
                        :loading="loading"
                        :hide-headers="$utils.isMobile()"
                    >
                        <template v-slot:items="props">
                            <tr v-if="$utils.isMobile()">
                                <td colspan="100" class="pa-0">
                                    <div class="dtm-cell mb-3 pa-2">
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[0].text}}</div>
                                            <div>{{ getIndexItem(props.index) }}</div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">Transaction No.</div>
                                            <div>{{ props.item.transaction_number }}</div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[1].text}}</div>
                                            <div>{{ props.item.created_time | formatDateTime }}</div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[2].text}}</div>
                                            <div :class="getStatusColor(props.item.transaction_status)">
                                                <b>{{getStatus(props.item.transaction_status) }}</b>
                                            </div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[3].text}}</div>
                                            <div>{{ $filters.formatNumber(props.item.amount, currentCurrency) }}</div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[4].text}}</div>
                                            <div>{{ genPackageInformation(props.item.package) }}</div>
                                        </div>
                                        <div class="dtm-row">
                                            <div class="dtm-header">{{headers[5].text}}</div>
                                            <div>{{ props.item.project.name }}</div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr v-else>
                                <td class="text-xs-center">{{ props.item.id }}</td>
                                <td class="text-xs-left">{{ props.item.created_time | formatDateTime }}</td>
                                <td class="text-xs-left" :class="getStatusColor(props.item.transaction_status)">
                                    <b>{{getStatus(props.item.transaction_status) }}</b>
                                </td>
                                <td class="text-xs-left" style="font-weight: bold;">
                                    {{ $filters.formatNumber(props.item.amount, currentCurrency) }}
                                </td>
                                <td class="text-xs-left">{{ genPackageInformation(props.item.package) }}</td>
                                <td class="text-xs-left">{{ props.item.project.name }}</td>
                                <td class="text-xs-left">{{ props.item.transaction_number }}</td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-flex>
            </v-layout>
        </v-container>
    </v-container>
</template>

<script>
import PaymentService from '@/services/payment.service'
import { mapState } from 'vuex'

export default {
    name: 'PaymentHistory',
    data () {
        return {
            displayFromDatePicker: false,
            displayToDatePicker: false,
            fromDate: null,
            toDate: this.$moment().format('YYYY-MM-DD'),
            today: this.$moment().format('YYYY-MM-DD'),
            currentCurrency: 'VND',
            dataTable: [],
            pagination: {
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
                rowsPerPageItems: [5, 10, 20, 50]
            },
            loading: false,
            projectId: ''
        }
    },
    computed: {
        ...mapState({
            projects: state => state.user.projects,
            token: state => state.user.token,
            currentProjectId: state => state.user.currentProjectId
        }),
        status () {
            return [
                { name: 'processing', description: this.$t('paymentStatus.processing'), textColor: 'warning--text' },
                { name: 'error', description: this.$t('paymentStatus.error'), textColor: 'error--text' },
                { name: 'success', description: this.$t('paymentStatus.success'), textColor: 'success--text' },
                { name: 'cancelled', description: this.$t('paymentStatus.cancelled'), textColor: 'gray--text' }
            ]
        },
        computedFromDateFormatted () {
            if (this.fromDate) {
                return this.formatDate(this.fromDate)
            } else {
                return ''
            }
        },
        computedToDateFormatted () {
            if (this.toDate) {
                return this.formatDate(this.toDate)
            } else {
                return ''
            }
        },
        headers () {
            return [
                { text: this.$filters.capitalize(this.$t('vnp_TxnRef')), align: 'center', sortable: false, value: 'vnp_TxnRef' },
                { text: this.$filters.capitalize(this.$t('date')), align: 'left', sortable: false, value: 'date' },
                { text: this.$filters.capitalize(this.$t('status')), align: 'left', sortable: false, value: 'date' },
                {
                    text: this.$filters.capitalize(this.$t('amount')) + ' (VND)',
                    align: 'left',
                    sortable: false,
                    value: 'amount'
                },
                { text: this.$filters.capitalize(this.$t('package')), align: 'left', sortable: false, value: 'package' },
                { text: this.$filters.capitalize(this.$t('project')), align: 'left', sortable: false, value: 'project' },
                { text: this.$filters.capitalize(this.$t('transactionNumber')), align: 'left', sortable: false, value: 'transaction_number' }
            ]
        }
    },
    async mounted () {
        this.getPaymentHistory()
    },
    methods: {
        getStatus (status) {
            for (let i = 0; i < this.status.length; i++) {
                if (status === this.status[i].name) {
                    return this.status[i].description
                }
            }
        },
        getStatusColor (status) {
            for (let i = 0; i < this.status.length; i++) {
                if (status === this.status[i].name) {
                    return this.status[i].textColor
                }
            }
        },
        formatDate (date) {
            if (!date) return null
            const [year, month, day] = date.split('-')
            return `${year}/${month}/${day}`
        },
        clearFromDate () {
            this.fromDate = null
        },
        clearToDate () {
            this.toDate = null
        },
        formatMoney (amount, currency) {
            try {
                // const negativeSign = amount < 0 ? '-' : ''
                let prefix = currency === 'USD' ? '$' : ''
                let suffix = currency === 'VND' ? '₫' : ''
                let thousands = currency === 'USD' ? ',' : '.'

                let i = parseInt(amount = Math.abs(Number(amount) || 0)).toString()
                let j = (i.length > 3) ? i.length % 3 : 0

                return prefix + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) + suffix
            } catch (e) {
                console.log(e)
            }
        },
        getPaymentHistory () {
            let todate = ''
            if (this.toDate) {
                todate = this.toDate + ' 23:59:59'
            }
            PaymentService.getPaymentHistory(
                this.pagination.rowsPerPage,
                this.pagination.page,
                this.fromDate,
                todate,
                this.projectId
            ).then(res => {
                this.dataTable = res.data.results
                this.pagination.totalItems = res.data.count
                this.$store.dispatch(this.$actionTypes.LOADING, false)
            })
        },
        getIndexItem (idx) {
            return this.pagination.rowsPerPage * (this.pagination.page - 1) + idx + 1
        },
        genPackageInformation (pkg) {
            return pkg.api_name + ' - ' + this.$filters.formatNumber(pkg.price, this.currentCurrency) + ' - ' + pkg.value + ' ' + pkg.unit
        }
    },
    watch: {
        pagination () {
            this.getPaymentHistory()
        }
    }
}
</script>
